import React, { useEffect, useState, Children, lazy, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Wind, Sun, Heart, CheckCircle2, Moon, Activity, Shield, Calendar, BookOpen, Utensils, Check, X } from 'lucide-react';
// Animation variants for consistent reveal effects
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 1,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function App() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  // Initialize Meta Pixel
  useEffect(() => {
    // Meta Pixel Code - Simplified and safer implementation
    if (window.fbq) return; // Already initialized
    const fbq: any = function () {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    // Create and append script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    // Initialize pixel after script is appended
    script.onload = () => {
      window.fbq('init', '855275007473565');
      window.fbq('track', 'PageView');
    };
    // Fallback if script loads before onload fires
    setTimeout(() => {
      if (window.fbq && window.fbq.loaded) {
        window.fbq('init', '855275007473565');
        window.fbq('track', 'PageView');
      }
    }, 100);
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  const handlePurchaseClick = () => {
    // Fire AddToCart event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Desbloquea tu Energía Femenina',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
    // Redirect to Shopify cart
    window.location.href = 'https://www.vitalhabitos.com/cart/47592665350301:1';
  };
  return <div className="min-h-screen bg-stone-100 text-stone-900 overflow-x-hidden selection:bg-terracotta/20 font-sans" data-id="element-0">
      {/* Navigation - Minimal & Clean */}
      <nav className="fixed top-0 w-full p-6 md:p-10 flex justify-between items-center z-50 mix-blend-multiply pointer-events-none" data-id="element-1">
        <div className="font-serif text-xl tracking-wide text-stone-900 pointer-events-auto" data-id="element-2">
          Vitalidad.
        </div>
        <button className="text-sm font-medium text-stone-800 hover:text-terracotta transition-colors duration-300 pointer-events-auto" data-id="element-3">
          Iniciar sesión
        </button>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center px-6 relative pt-20 md:pt-0" data-id="element-4">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-5">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center lg:text-left order-2 lg:order-1" data-id="element-6">
            <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl lg:text-8xl font-serif leading-[1.1] mb-8 text-stone-900" data-id="element-7">
              No te falta disciplina. <br data-id="element-8" />
              <span className="italic text-stone-800/80" data-id="element-9">
                Te falta energía.
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-stone-800 max-w-xl mx-auto lg:mx-0 mb-12 font-light" data-id="element-10">
              Has intentado empujar más fuerte, pero el cuerpo dice basta. No es
              pereza, es una señal. Escuchemos lo que intenta decirte.
            </motion.p>

            <motion.div variants={fadeInUp} data-id="element-11">
              <button onClick={scrollToPricing} className="bg-terracotta text-stone-50 px-8 py-4 rounded-full text-lg font-medium hover:bg-terracotta-hover transition-colors duration-300 flex items-center gap-2 mx-auto lg:mx-0 group shadow-lg shadow-terracotta/20" data-id="element-12">
                Comenzar el cambio
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-13" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="relative order-1 lg:order-2" data-id="element-14">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-stone-300/50 aspect-[4/5] md:aspect-square lg:aspect-[4/5] max-w-md mx-auto lg:max-w-none" data-id="element-15">
              <img src="https://cdn.magicpatterns.com/uploads/t1qxADSaMJK1tPjsUYaXGt/Imagen_2.png" alt="Mujer disfrutando un momento de calma con su café y el ebook" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="eager" data-id="element-16" />
              <div className="absolute inset-0 bg-stone-900/5 pointer-events-none" data-id="element-17"></div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden lg:block" data-id="element-18">
          <div className="w-[1px] h-16 bg-stone-300 overflow-hidden" data-id="element-19">
            <div className="w-full h-1/2 bg-stone-800 animate-scroll-down" data-id="element-20"></div>
          </div>
        </motion.div>
      </section>

      {/* Deep Empathy Section */}
      <section className="py-24 px-6 bg-stone-50" data-id="element-21">
        <div className="max-w-4xl mx-auto" data-id="element-22">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} data-id="element-23">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-12 text-center text-stone-900" data-id="element-24">
              ¿Te reconoces en esto?
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-25">
              <motion.div variants={fadeInUp} className="space-y-6" data-id="element-26">
                <EmpathyItem text="Te levantas cansada aunque hayas dormido 8 horas." data-id="element-27" />
                <EmpathyItem text="Tu mente está en una niebla constante que no te deja concentrarte." data-id="element-28" />
                <EmpathyItem text="Te irritas por cosas pequeñas y luego te sientes culpable." data-id="element-29" />
              </motion.div>
              <motion.div variants={fadeInUp} className="space-y-6 md:mt-12" data-id="element-30">
                <EmpathyItem text="Sientes una desconexión total de tu cuerpo y sus necesidades." data-id="element-31" />
                <EmpathyItem text="Has probado dietas y rutinas estrictas que abandonas a los 3 días." data-id="element-32" />
                <EmpathyItem text="Vives esperando el fin de semana solo para dormir." data-id="element-33" />
              </motion.div>
            </div>

            <motion.p variants={fadeInUp} className="text-center text-lg text-stone-600 mt-12 italic max-w-2xl mx-auto" data-id="element-34">
              No es tu culpa. Es tu sistema nervioso pidiendo una pausa real, no
              solo "descanso".
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* The Truth Section */}
      <section className="py-32 px-6 bg-stone-100" data-id="element-35">
        <div className="max-w-3xl mx-auto" data-id="element-36">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} data-id="element-37">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-10 text-stone-900" data-id="element-38">
              La mentira de la fuerza de voluntad
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-8 text-lg md:text-xl font-light text-stone-800" data-id="element-39">
              <p data-id="element-40">
                Nos han enseñado que si no logramos nuestros objetivos, es
                porque no nos esforzamos lo suficiente. Que debemos levantarnos
                a las 5 AM, beber café y empujar hasta el agotamiento.
              </p>
              <p data-id="element-41">
                Pero la realidad es diferente. Cuando tu sistema nervioso está
                agotado, la "disciplina" es solo otra forma de violencia contra
                ti mismo.
              </p>
              <p className="text-xl md:text-2xl font-serif italic text-terracotta pt-4" data-id="element-42">
                No necesitas más reglas. Necesitas recuperar tu vitalidad
                biológica.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mechanism Explanation Section */}
      <section className="py-32 px-6 bg-stone-50" data-id="element-43">
        <div className="max-w-6xl mx-auto" data-id="element-44">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-45">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-6 text-stone-900" data-id="element-46">
              Por qué los microhábitos funcionan
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600 max-w-2xl mx-auto" data-id="element-47">
              La armonización hormonal no requiere cambios drásticos. Requiere
              señales constantes de seguridad para tu cuerpo.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-48">
            {/* Image for Mechanism */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="lg:col-span-5 order-2 lg:order-1" data-id="element-49">
              <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-stone-200/50 aspect-[4/5]" data-id="element-50">
                <img src="https://cdn.magicpatterns.com/uploads/1JvLS1pSbXK2SCwFhnnEWC/Imagen_3.png" alt="Detalle del ebook en tablet mostrando diseño sereno" className="w-full h-full object-cover" loading="lazy" data-id="element-51" />
                <div className="absolute inset-0 bg-stone-900/5" data-id="element-52"></div>
              </div>
            </motion.div>

            {/* Cards */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-6 order-1 lg:order-2" data-id="element-53">
              <MechanismCard icon={<Shield className="w-6 h-6" data-id="element-55" />} title="Sin Motivación Extrema" description="Las acciones pequeñas no activan la resistencia mental. No necesitas 'ganas' para hacer algo que toma 2 minutos." delay={0.1} data-id="element-54" />
              <MechanismCard icon={<Activity className="w-6 h-6" data-id="element-57" />} title="Estabilización Hormonal" description="Reducimos los picos de cortisol y glucosa suavemente, permitiendo que tu energía se mantenga estable todo el día." delay={0.2} data-id="element-56" />
              <MechanismCard icon={<Moon className="w-6 h-6" data-id="element-59" />} title="Ritmos Naturales" description="Trabajamos a favor de tu reloj biológico, no en contra. El sueño profundo y la energía diurna son consecuencias naturales." delay={0.3} data-id="element-58" />
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-32 px-6 bg-stone-100" data-id="element-60">
        <div className="max-w-5xl mx-auto" data-id="element-61">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-62">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-63">
              Un retorno a lo natural
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto" data-id="element-64">
              Dejamos de luchar contra el cuerpo y empezamos a trabajar con él.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8" data-id="element-65">
            <Feature icon={<Wind className="w-6 h-6" data-id="element-67" />} title="Respiración" description="Aprende a usar tu respiración para cambiar tu estado nervioso en minutos, no en años." delay={0.1} data-id="element-66" />
            <Feature icon={<Sun className="w-6 h-6" data-id="element-69" />} title="Ritmos" description="Alinea tu descanso y actividad con la luz del sol. Tu biología espera señales, dáselas." delay={0.2} data-id="element-68" />
            <Feature icon={<Heart className="w-6 h-6" data-id="element-71" />} title="Conexión" description="La energía no solo viene de la comida, viene de sentirte seguro en tu propio cuerpo." delay={0.3} data-id="element-70" />
          </div>
        </div>
      </section>

      {/* Timeline Benefits Section */}
      <section className="py-32 px-6 bg-stone-50 overflow-hidden" data-id="element-72">
        <div className="max-w-5xl mx-auto" data-id="element-73">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-74">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 text-stone-900" data-id="element-75">
              Tu camino de recuperación
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto" data-id="element-76">
              Lo que puedes esperar cuando dejas de forzar y empiezas a fluir.
            </p>
          </motion.div>

          <div className="relative" data-id="element-77">
            {/* Connecting Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-stone-200 -translate-x-1/2 hidden md:block" data-id="element-78"></div>
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-stone-200 md:hidden" data-id="element-79"></div>

            <div className="space-y-12 md:space-y-24 relative" data-id="element-80">
              <TimelineItem day="Día 7" title="El despertar suave" benefits={['Menos cansancio al sonar la alarma', 'Primeras señales de energía estable por la tarde', 'Disminución de la ansiedad nocturna']} align="left" data-id="element-81" />
              <TimelineItem day="Día 14" title="Claridad mental" benefits={['La niebla mental empieza a disiparse', 'Menos irritabilidad con tu entorno', 'Capacidad de concentrarte por periodos más largos']} align="right" data-id="element-82" />
              <TimelineItem day="Día 21" title="Energía renovada" benefits={['Energía estable durante todo el día', 'Sueño profundo y verdaderamente reparador', 'Te sientes tú misma otra vez, conectada y en calma']} align="left" isLast data-id="element-83" />
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-40 px-6 bg-stone-900 text-stone-100 text-center" data-id="element-84">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto" data-id="element-85">
          <p className="text-3xl md:text-5xl font-serif leading-tight mb-8" data-id="element-86">
            "La verdadera productividad es el resultado natural de un cuerpo
            descansado y una mente en calma."
          </p>
        </motion.div>
      </section>

      {/* Enhanced Product CTA Section */}
      <section id="pricing" className="py-32 px-6 bg-stone-100 flex flex-col items-center scroll-mt-20" data-id="element-87">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-6xl w-full" data-id="element-88">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-stone-200/50 border border-stone-100 overflow-hidden" data-id="element-89">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-90">
              {/* Product Image */}
              <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3] lg:aspect-square" data-id="element-91">
                <img src="https://cdn.magicpatterns.com/uploads/42KB1AoPRRpqMvYMjcRGyx/Imagen_1.png" alt="Ebook Desbloquea tu Energía Femenina en tablet" className="w-full h-full object-cover" loading="lazy" data-id="element-92" />
              </motion.div>

              {/* Product Details */}
              <div className="space-y-8" data-id="element-93">
                <div data-id="element-94">
                  <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-4 text-stone-900 leading-tight" data-id="element-95">
                    Desbloquea tu <br data-id="element-96" />
                    <span className="text-terracotta" data-id="element-97">Energía Femenina</span>
                  </motion.h2>
                  <motion.p variants={fadeInUp} className="text-lg text-stone-600" data-id="element-98">
                    21 días de microhábitos para volver a sentirte tú, sin
                    dietas extremas ni rutinas imposibles.
                  </motion.p>
                </div>

                <motion.div variants={fadeInUp} className="space-y-4" data-id="element-99">
                  <h3 className="font-medium text-stone-900 uppercase tracking-wide text-sm" data-id="element-100">
                    Lo que incluye:
                  </h3>
                  <ul className="space-y-3" data-id="element-101">
                    <BonusItem icon={<BookOpen className="w-5 h-5" data-id="element-103" />} text="Ebook Completo: El sistema paso a paso" data-id="element-102" />
                    <BonusItem icon={<Check className="w-5 h-5" data-id="element-105" />} text="Bonus #1: Checklist de hábitos imprimible" data-id="element-104" />
                    <BonusItem icon={<Utensils className="w-5 h-5" data-id="element-107" />} text="Bonus #2: Mini-guía de recetas energizantes" data-id="element-106" />
                    <BonusItem icon={<Calendar className="w-5 h-5" data-id="element-109" />} text="Bonus #3: Diario/Tracker de 21 días" data-id="element-108" />
                  </ul>
                </motion.div>

                <div className="bg-stone-50 rounded-2xl p-6 md:p-8 border border-stone-100" data-id="element-110">
                  <motion.div variants={fadeInUp} data-id="element-111">
                    <div className="flex justify-between items-end mb-6" data-id="element-112">
                      <div data-id="element-113">
                        <p className="text-stone-500 text-sm font-medium uppercase tracking-wider mb-1" data-id="element-114">
                          Oferta Especial
                        </p>
                        <div className="flex items-baseline gap-2" data-id="element-115">
                          <span className="text-4xl md:text-5xl font-serif text-stone-900" data-id="element-116">
                            $6.39
                          </span>
                          <span className="text-stone-400 line-through text-xl" data-id="element-117">
                            $19.99
                          </span>
                        </div>
                      </div>
                    </div>

                    <button onClick={handlePurchaseClick} className="w-full bg-terracotta text-stone-50 px-6 py-4 rounded-xl text-lg font-medium hover:bg-terracotta-hover transition-all duration-300 shadow-lg shadow-terracotta/20 hover:shadow-xl hover:scale-[1.02] mb-4" data-id="element-118">
                      Comenzar mi transformación
                    </button>

                    <div className="space-y-2 text-xs text-stone-500 text-center" data-id="element-119">
                      <p className="flex items-center justify-center gap-1" data-id="element-120">
                        <Shield className="w-3 h-3" data-id="element-121" /> Pago 100% seguro
                      </p>
                      <p data-id="element-122">Acceso inmediato • Garantía de satisfacción</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 text-center bg-stone-100 border-t border-stone-200" data-id="element-123">
        <div className="max-w-4xl mx-auto" data-id="element-124">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-4" data-id="element-125">
            <button onClick={() => setModalContent('privacy')} className="text-stone-600 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-126">
              Política de Privacidad
            </button>
            <span className="hidden md:block text-stone-300" data-id="element-127">•</span>
            <button onClick={() => setModalContent('terms')} className="text-stone-600 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-128">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-stone-400 text-sm" data-id="element-129">
            &copy; {new Date().getFullYear()} Vitalidad. Hecho con intención.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-130">
        {modalContent && <Modal onClose={() => setModalContent(null)} data-id="element-131">
            {modalContent === 'privacy' ? <PrivacyContent data-id="element-132" /> : <TermsContent data-id="element-133" />}
          </Modal>}
      </AnimatePresence>
    </div>;
}
// Helper Components
function EmpathyItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-4" data-id="element-134">
      <div className="mt-1 bg-terracotta/10 p-1 rounded-full" data-id="element-135">
        <CheckCircle2 className="w-5 h-5 text-terracotta" data-id="element-136" />
      </div>
      <p className="text-lg text-stone-700 font-light leading-relaxed" data-id="element-137">
        {text}
      </p>
    </div>;
}
function MechanismCard({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-stone-100 hover:shadow-md transition-shadow duration-300 flex flex-col md:flex-row gap-6 items-start" data-id="element-138">
      <div className="w-12 h-12 rounded-full bg-stone-100 flex-shrink-0 flex items-center justify-center text-stone-800" data-id="element-139">
        {icon}
      </div>
      <div data-id="element-140">
        <h3 className="text-xl font-serif mb-2 text-stone-900" data-id="element-141">{title}</h3>
        <p className="text-stone-600 leading-relaxed font-light" data-id="element-142">
          {description}
        </p>
      </div>
    </motion.div>;
}
function Feature({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="flex flex-col items-center text-center md:items-start md:text-left" data-id="element-143">
      <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center text-stone-800 mb-6" data-id="element-144">
        {icon}
      </div>
      <h3 className="text-2xl font-serif mb-3 text-stone-900" data-id="element-145">{title}</h3>
      <p className="text-stone-600 leading-relaxed" data-id="element-146">{description}</p>
    </motion.div>;
}
function TimelineItem({
  day,
  title,
  benefits,
  align,
  isLast
}: {
  day: string;
  title: string;
  benefits: string[];
  align: 'left' | 'right';
  isLast?: boolean;
}) {
  const isLeft = align === 'left';
  return <motion.div initial={{
    opacity: 0,
    x: isLeft ? -20 : 20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true,
    margin: '-10%'
  }} transition={{
    duration: 0.8
  }} className={`flex flex-col md:flex-row items-center md:justify-between gap-8 md:gap-0 ${isLeft ? '' : 'md:flex-row-reverse'}`} data-id="element-147">
      {/* Content Side */}
      <div className={`w-full md:w-[45%] pl-12 md:pl-0 ${isLeft ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`} data-id="element-148">
        <span className="inline-block px-3 py-1 bg-terracotta/10 text-terracotta rounded-full text-sm font-medium mb-3" data-id="element-149">
          {day}
        </span>
        <h3 className="text-2xl font-serif text-stone-900 mb-4" data-id="element-150">{title}</h3>
        <ul className={`space-y-2 ${isLeft ? 'md:items-end' : 'md:items-start'} flex flex-col`} data-id="element-151">
          {benefits.map((benefit, idx) => <li key={idx} className="text-stone-600 font-light flex items-center gap-2" data-id="element-152">
              <span className="w-1.5 h-1.5 rounded-full bg-stone-300 flex-shrink-0 md:hidden" data-id="element-153"></span>
              {benefit}
              <span className={`w-1.5 h-1.5 rounded-full bg-stone-300 flex-shrink-0 hidden md:block ${isLeft ? '' : 'order-first'}`} data-id="element-154"></span>
            </li>)}
        </ul>
      </div>

      {/* Center Marker */}
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-terracotta bg-stone-50 z-10" data-id="element-155">
        <div className="w-2 h-2 bg-terracotta rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-156"></div>
      </div>

      {/* Empty Side for Balance */}
      <div className="hidden md:block w-[45%]" data-id="element-157"></div>
    </motion.div>;
}
function BonusItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <li className="flex items-center gap-3 text-stone-700" data-id="element-158">
      <div className="text-terracotta bg-terracotta/5 p-1.5 rounded-md" data-id="element-159">
        {icon}
      </div>
      <span className="font-light" data-id="element-160">{text}</span>
    </li>;
}
// Modal Component
function Modal({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-161">
      <motion.div initial={{
      scale: 0.95,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.95,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-162">
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex justify-between items-center" data-id="element-163">
          <h2 className="text-2xl font-serif text-stone-900" data-id="element-164">
            {children && (children as any).type === PrivacyContent ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors duration-300" data-id="element-165">
            <X className="w-5 h-5 text-stone-600" data-id="element-166" />
          </button>
        </div>
        <div className="px-6 py-8 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-167">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
// Privacy Content
function PrivacyContent() {
  return <div className="prose prose-stone max-w-none" data-id="element-168">
      <p className="text-stone-600 mb-6" data-id="element-169">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-170">
        1. Información que Recopilamos
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-171">
        Al adquirir "Desbloquea tu Energía Femenina", recopilamos únicamente la
        información necesaria para procesar tu compra y entregarte el producto
        digital: nombre, correo electrónico e información de pago (procesada de
        forma segura por nuestro proveedor de pagos).
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-172">
        2. Uso de la Información
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-173">Utilizamos tu información para:</p>
      <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-2" data-id="element-174">
        <li data-id="element-175">Procesar y entregar tu compra del ebook</li>
        <li data-id="element-176">Enviarte el acceso inmediato al contenido digital</li>
        <li data-id="element-177">Proporcionar soporte relacionado con tu compra</li>
        <li data-id="element-178">
          Enviarte actualizaciones importantes sobre el producto (opcional)
        </li>
      </ul>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-179">
        3. Protección de Datos
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-180">
        Implementamos medidas de seguridad para proteger tu información
        personal. No compartimos, vendemos ni alquilamos tus datos a terceros.
        Tu información de pago es procesada de forma segura por proveedores
        certificados y nunca almacenamos datos de tarjetas de crédito.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-181">
        4. Tus Derechos
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-182">
        Tienes derecho a acceder, corregir o eliminar tu información personal en
        cualquier momento. Para ejercer estos derechos, contáctanos y
        responderemos en un plazo de 48 horas.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-183">5. Cookies</h3>
      <p className="text-stone-700 mb-6" data-id="element-184">
        Utilizamos cookies esenciales para el funcionamiento del sitio y para
        mejorar tu experiencia de compra. No utilizamos cookies de seguimiento
        publicitario.
      </p>
    </div>;
}
// Terms Content
function TermsContent() {
  return <div className="prose prose-stone max-w-none" data-id="element-185">
      <p className="text-stone-600 mb-6" data-id="element-186">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-187">
        1. Aceptación de Términos
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-188">
        Al adquirir "Desbloquea tu Energía Femenina", aceptas estos términos y
        condiciones. Este ebook es un producto digital diseñado para uso
        personal y no puede ser redistribuido, revendido o compartido.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-189">
        2. Licencia de Uso
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-190">
        Se te otorga una licencia personal, no transferible y no exclusiva para
        usar el ebook y sus materiales complementarios (checklists, guías,
        tracker). No puedes:
      </p>
      <ul className="list-disc pl-6 text-stone-700 mb-6 space-y-2" data-id="element-191">
        <li data-id="element-192">Compartir, copiar o distribuir el contenido</li>
        <li data-id="element-193">Modificar o crear trabajos derivados</li>
        <li data-id="element-194">Usar el contenido con fines comerciales</li>
        <li data-id="element-195">Revender o transferir tu acceso a terceros</li>
      </ul>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-196">
        3. Entrega del Producto
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-197">
        Recibirás acceso inmediato al ebook y todos los bonuses digitales tras
        completar tu compra. El acceso se enviará al correo electrónico
        proporcionado. Si no recibes el acceso en 10 minutos, verifica tu
        carpeta de spam o contáctanos.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-198">
        4. Garantía de Satisfacción
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-199">
        Ofrecemos una garantía de satisfacción de 14 días. Si el contenido no
        cumple con tus expectativas, contáctanos para solicitar un reembolso
        completo. Solo pedimos que nos compartas brevemente tu experiencia para
        mejorar el producto.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-200">
        5. Descargo de Responsabilidad
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-201">
        Este ebook proporciona información educativa sobre bienestar y
        microhábitos. No sustituye el consejo médico profesional. Consulta con
        un profesional de la salud antes de realizar cambios significativos en
        tu estilo de vida, especialmente si tienes condiciones médicas
        preexistentes.
      </p>

      <h3 className="text-xl font-serif text-stone-900 mb-4" data-id="element-202">
        6. Propiedad Intelectual
      </h3>
      <p className="text-stone-700 mb-6" data-id="element-203">
        Todo el contenido del ebook, incluyendo texto, imágenes, diseño y
        materiales complementarios, está protegido por derechos de autor.
        Cualquier uso no autorizado constituye una violación de estos derechos.
      </p>
    </div>;
}